import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Styled
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  z-index: 10;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

const OuterWrapper = styled.div`
  position: relative;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

// Colors: purple (current), red (previous), light purple (average)
const CURRENT_PURPLE = "#9848c7";
const CURRENT_PURPLE_FILL = "rgba(152, 72, 199, 0.15)";
const PREV_RED = "rgba(220, 53, 69, 1)";
const PREV_RED_FILL = "rgba(220, 53, 69, 0.15)";
const AVG_LIGHT_PURPLE = "rgba(152, 72, 199, 0.5)"; // average line

const ScatterPlotZakupy = ({ data }) => {
  const [salesData, setSalesData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const getWeekNumber = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return null;
    const firstDay = new Date(date.getFullYear(), 0, 1);
    const dayOfYear = Math.floor((date - firstDay) / 86400000) + 1;
    return Math.ceil(dayOfYear / 7);
  };

  const calculatePurchase = (row) => parseFloat(row.K_42) || 0;

  useEffect(() => {
    if (!data || data.length === 0) return;

    const grouped = data.reduce((acc, row) => {
      const year = parseInt(row.Rok, 10);
      const week = getWeekNumber(row.DataZakupu);
      const purchase = calculatePurchase(row);
      if (!year || !week) return acc;
      if (!acc[year]) acc[year] = {};
      if (!acc[year][week]) acc[year][week] = 0;
      acc[year][week] += purchase;
      return acc;
    }, {});

    const years = Object.keys(grouped).map(Number).sort((a, b) => a - b);
    setAvailableYears(years);
    if (!selectedYear && years.length) setSelectedYear(years[years.length - 1]);
    setSalesData(grouped);
  }, [data, selectedYear]);

  useEffect(() => {
    if (!selectedYear || !salesData[selectedYear]) return;

    const currentObj = salesData[selectedYear] || {};
    const previousObj = salesData[selectedYear - 1] || {};

    const weeks = [...new Set(
      [...Object.keys(currentObj), ...Object.keys(previousObj)].map(Number)
    )].sort((a, b) => a - b);

    const currentVals = weeks.map((w) => currentObj[w] ?? 0);
    const previousVals = weeks.map((w) => previousObj[w] ?? null);

    const nonZero = currentVals.filter((v) => v > 0);
    const avg = nonZero.length ? nonZero.reduce((s, v) => s + v, 0) / nonZero.length : 0;

    const currentPoints = weeks.map((w, i) => ({ x: w, y: currentVals[i] }));
    const previousPoints = weeks
      .map((w, i) => (previousVals[i] !== null ? { x: w, y: previousVals[i] } : null))
      .filter(Boolean);

    const maxWeek = Math.max(52, weeks[weeks.length - 1] || 52);
    const avgLine = Array.from({ length: maxWeek }, (_, i) => ({ x: i + 1, y: avg }));

    setChartData({
      datasets: [
        {
          label: `Zakupy (${selectedYear})`,
          data: currentPoints,
          type: "line",
          borderColor: CURRENT_PURPLE,
          backgroundColor: CURRENT_PURPLE_FILL,
          borderWidth: 2,
          pointRadius: 2,
          fill: false,
        },
        {
          label: `Zakupy (${selectedYear - 1})`,
          data: previousPoints,
          type: "line",
          borderColor: PREV_RED,
          backgroundColor: PREV_RED_FILL,
          borderWidth: 2,
          pointRadius: 2,
          fill: false,
        },
        {
          label: `Średnia (${selectedYear})`,
          data: avgLine,
          type: "line",
          borderColor: AVG_LIGHT_PURPLE,
          borderDash: [8, 4],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
        },
      ],
    });
  }, [selectedYear, salesData]);

  if (!availableYears.length || !chartData) return <p>Ładowanie danych...</p>;

  return (
    <OuterWrapper>
      <InfoIconWrapper data-tooltip-id="scatter-zakupy-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="scatter-zakupy-tooltip"
        place="left"
        content={
          <>
            <strong>Porównianie średnich tygodniowych zakupów</strong><br />
            Linie: fiolet – bieżący rok, czerwony – poprzedni rok, jasny fiolet – średnia {selectedYear}.
          </>
        }
      />
      <ChartWrapper>
        <FiltersWrapper>
          <div>
            <label htmlFor="year-select">Rok:&nbsp;</label>
            <select
              id="year-select"
              value={selectedYear || ""}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </FiltersWrapper>

        <Scatter
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            parsing: false,
            scales: {
              x: {
                type: "linear",
                min: 1,
                max: 52,
                title: { display: true, text: "Tydzień" },
                ticks: {
                  stepSize: 1,
                  callback: (val) => `Tydz. ${val}`,
                },
              },
              y: {
                beginAtZero: true,
                title: { display: true, text: "Zakupy (PLN)" },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Porównianie średnich tygodniowych zakupów – ${selectedYear} vs ${selectedYear - 1}`,
              },
              legend: { position: "top" },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const ds = ctx.dataset.label || "";
                    const y = ctx.raw?.y ?? 0;
                    return `${ds}: ${y.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  },
                },
              },
            },
          }}
        />
      </ChartWrapper>
    </OuterWrapper>
  );
};

export default ScatterPlotZakupy;
